import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { SERVICES, SITE } from "../data/site";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/profile", label: "Profile" },
  { to: "/solutions", label: "Solutions" },
  { to: "/services", label: "Services", hasMenu: true },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/60"
    >
      <div className="container-st flex items-center justify-between h-20">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
          <img src={SITE.logo} alt="Amcblast Solutions Limited" className="h-10 w-auto object-contain" />
          <span className="sr-only">Amcblast Solutions Limited</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {navLinks.map((l) =>
            l.hasMenu ? (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <NavLink
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-full text-sm font-medium inline-flex items-center gap-1 transition ${
                      isActive
                        ? "text-blue-600 bg-blue-50"
                        : "text-slate-700 hover:text-blue-600 hover:bg-slate-50"
                    }`
                  }
                >
                  {l.label} <ChevronDown className="w-4 h-4" />
                </NavLink>
                {servicesOpen && (
                  <div className="absolute left-0 top-full pt-2 w-[34rem]">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-3 grid grid-cols-2 gap-1">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          data-testid={`menu-service-${s.slug}`}
                          className="block px-3 py-2 rounded-xl text-sm text-slate-700 hover:bg-slate-50 hover:text-blue-600"
                        >
                          {s.title}
                        </Link>
                      ))}
                      <Link
                        to="/services"
                        className="col-span-2 block px-3 py-2 mt-1 text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded-xl"
                      >
                        View all services →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition ${
                    isActive
                      ? "text-blue-600 bg-blue-50"
                      : "text-slate-700 hover:text-blue-600 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <Link
            to="/contact?type=quote"
            data-testid="get-quote-btn"
            className="btn-shine inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-3 rounded-full transition"
          >
            Get a Job Quote
          </Link>
        </div>

        <button
          className="xl:hidden p-2"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-slate-200 bg-white max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="container-st py-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-sm font-medium ${
                    isActive ? "text-blue-600 bg-blue-50" : "text-slate-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact?type=quote"
              onClick={() => setOpen(false)}
              data-testid="mobile-get-quote-btn"
              className="mt-2 inline-flex items-center justify-center bg-blue-600 text-white font-semibold px-5 py-3 rounded-full"
            >
              Get a Job Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
